/**
 * API Endpoints Registry
 * Centralized registry for all API endpoint paths
 * All URLs are defined in one place for easy maintenance
 */

/**
 * API Endpoints
 */
export const API_ENDPOINTS = {
  // Project Endpoints - Backend route: /api/v1/process/projects
  PROJECTS: {
    LIST: '/api/v1/project/projects/list',
    GET_BY_ID: '/api/v1/project/projects/by-id',
    CREATE: '/api/v1/project/projects',
    UPDATE: (id: number) => `/api/v1/project/projects/${id}`,
    DELETE: (id: number) => `/api/v1/project/projects/${id}`,
  },

  // ProcessStudio Endpoints - Orchestration for creating complete project structures
  STUDIO_PROJECTS: {
    CREATE_WITH_STRUCTURE: '/api/v1/process-studio/studio-projects/create-with-structure',
  },

  // Workflow Save Endpoints - Atomic workflow save operations
  WORKFLOWS: {
    SAVE: '/api/v1/process-studio/workflows/save',
    REFRESH: (processThreadId: number) =>
      `/api/v1/process-studio/workflows/refresh/${processThreadId}`,
  },

  // App Endpoints
  APPS: {
    LIST: '/api/ai-extension/apps/list',
    GET_BY_ID: '/api/ai-extension/apps/get-by-id',
    CREATE: '/api/ai-extension/apps',
    UPDATE: (id: number) => `/api/ai-extension/apps/${id}`,
    DELETE: (id: number) => `/api/ai-extension/apps/${id}`,
    BY_TYPE: '/api/ai-extension/apps/by-type',
    ACTIVE: '/api/ai-extension/apps/active',
    ACTIVATE: '/api/ai-extension/apps/activate',
    DEACTIVATE: '/api/ai-extension/apps/deactivate',
    VALIDATE: '/api/ai-extension/apps/validate',
    NAME_EXISTS: '/api/ai-extension/apps/name-exists',
  },

  // Process Endpoints - Backend route: /api/v1/process/processes
  PROCESSES: {
    LIST: '/api/v1/process/processes/list',
    GET_BY_ID: '/api/v1/process/processes/by-id',
    CREATE: '/api/v1/process/processes',
    UPDATE: (id: number) => `/api/v1/process/processes/${id}`,
    DELETE: (id: number) => `/api/v1/process/processes/${id}`,
    BY_PROJECT: '/api/v1/process/processes/by-project',
    BY_TYPE: '/api/v1/process/processes/by-process-type',
    BY_CATEGORY: '/api/v1/process/processes/by-category',
    TEMPLATES: '/api/v1/process/processes/templates',
    PUBLISHED: '/api/v1/process/processes/published',
    ENABLED: '/api/v1/process/processes/enabled',
  },

  // ProcessThread Endpoints - Backend route: /api/v1/process/process-threads
  PROCESS_THREADS: {
    LIST: '/api/v1/process/process-threads/list',
    GET_BY_ID: '/api/v1/process/process-threads/by-id',
    CREATE: '/api/v1/process/process-threads',
    UPDATE: (id: number) => `/api/v1/process/process-threads/${id}`,
    DELETE: (id: number) => `/api/v1/process/process-threads/${id}`,
    BY_PROCESS: '/api/v1/process/process-threads/by-process',
    BY_TYPE: '/api/v1/process/process-threads/by-type',
    BY_CATEGORY: '/api/v1/process/process-threads/by-category',
    ACTIVE: '/api/v1/process/process-threads/active',
    PUBLISHED: '/api/v1/process/process-threads/published',
    TEMPLATES: '/api/v1/process/process-threads/templates',
    ENABLED: '/api/v1/process/process-threads/enabled',
    LOCKED: '/api/v1/process/process-threads/locked',
    BY_CURRENT_EDITOR: '/api/v1/process/process-threads/by-current-editor',
  },

  // ProcessElement Endpoints - Backend route: /api/v1/process/process-elements
  PROCESS_ELEMENTS: {
    LIST: '/api/v1/process/process-elements/list',
    GET_BY_ID: '/api/v1/process/process-elements/by-id',
    CREATE: '/api/v1/process/process-elements',
    UPDATE: (id: number) => `/api/v1/process/process-elements/${id}`,
    DELETE: (id: number) => `/api/v1/process/process-elements/${id}`,
    BY_THREAD_VERSION: '/api/v1/process/process-elements/by-thread-version',
    BY_ELEMENT_TYPE: '/api/v1/process/process-elements/by-process-element-type',
    BY_EXECUTION_MODE: '/api/v1/process/process-elements/by-execution-mode',
    BY_GROUP: '/api/v1/process/process-elements/by-group',
    TRIGGERS: '/api/v1/process/process-elements/triggers',
    BY_KEY: '/api/v1/process/process-elements/by-key',
  },

  // Connection Endpoints - Backend route: /api/v1/process/connections
  CONNECTIONS: {
    LIST: '/api/v1/process/connections/list',
    GET_BY_ID: '/api/v1/process/connections/by-id',
    CREATE: '/api/v1/process/connections',
    UPDATE: (id: number) => `/api/v1/process/connections/${id}`,
    DELETE: (id: number) => `/api/v1/process/connections/${id}`,
    BY_THREAD_VERSION: '/api/v1/process/connections/by-thread-version',
    BY_SOURCE_ELEMENT: '/api/v1/process/connections/by-source-element',
    BY_TARGET_ELEMENT: '/api/v1/process/connections/by-target-element',
    BY_CONNECTION_TYPE: '/api/v1/process/connections/by-connection-type',
    CONDITIONAL: '/api/v1/process/connections/conditional',
    BY_EDGE_TYPE: '/api/v1/process/connections/by-edge-type',
    BY_EDGE_STYLE: '/api/v1/process/connections/by-edge-style',
  },

  // Connector Endpoints (AI Extension)
  // NOTE: Create/Update/Delete operations are handled by the workflow save process
  // Connectors are automatically created/updated when saving workflows
  CONNECTORS: {
    LIST: '/api/v1/ai-extension/connectors/list',
    GET_BY_ID: '/api/v1/ai-extension/connectors/get-by-id',
    ACTIVE: '/api/v1/ai-extension/connectors/active',
    UPDATE: (id: number) => `/api/v1/ai-extension/connectors/${id}`,
    TEST_CONNECTION: (id: number) => `/api/v1/ai-extension/connectors/${id}/test`,
  },

  // ExtensionConfig Endpoints (AI Extension)
  EXTENSION_CONFIGS: {
    LIST: '/api/v1/ai-extension/extension-configs/list',
    GET_BY_ID: '/api/v1/ai-extension/extension-configs/get-by-id',
    BY_EXTENSION: '/api/v1/ai-extension/extension-configs/by-extension',
    CREATE: '/api/v1/ai-extension/extension-configs',
    UPDATE: (id: number) => `/api/v1/ai-extension/extension-configs/${id}`,
  },

  // Credential Endpoints (AI Extension)
  CREDENTIALS: {
    LIST: '/api/v1/ai-extension/credentials/list',
    GET_BY_ID: '/api/v1/ai-extension/credentials/get-by-id',
    GET_BY_ID_DECRYPTED: '/api/v1/ai-extension/credentials/get-by-id/decrypted',
    ACTIVE: '/api/v1/ai-extension/credentials/active',
    BY_TYPE: '/api/v1/ai-extension/credentials/by-type',
    BY_TYPE_CODE: '/api/v1/ai-extension/credentials/by-type-code',
    UPDATE_DISPLAY_METADATA: '/api/v1/ai-extension/credentials/update-display-metadata',
    CREATE: '/api/v1/ai-extension/credentials',
    UPDATE: (id: number) => `/api/v1/ai-extension/credentials/${id}`,
    DELETE: (id: number) => `/api/v1/ai-extension/credentials/${id}`,
    FORM_SCHEMA: '/api/v1/ai-extension/credentials/form-schema',
    EXCHANGE_OAUTH_CODE: '/api/v1/ai-extension/credentials/exchange-oauth-code',
  },

  // Credential Type Endpoints (AI Extension)
  CREDENTIAL_TYPES: {
    LIST: '/api/v1/ai-extension/credential-types/list',
    GET_BY_ID: '/api/v1/ai-extension/credential-types/get-by-id',
    BY_CODE: '/api/v1/ai-extension/credential-types/by-code',
  },

  // Settings / Form Schema Endpoints (Phase 2: Form Builder Integration)
  SETTINGS: {
    FORM_SCHEMA: '/api/v1/process-studio/form-builder/form-schema',
  },

  // Form Builder Endpoints (Phase 2: Form Builder Integration - ProcessStudio)
  FORM_BUILDER: {
    BY_EXTENSION: '/api/v1/process-studio/form-builder/by-extension',
    BY_CONNECTOR: '/api/v1/process-studio/form-builder/by-connector',
    HEALTH: '/api/v1/process-studio/form-builder/health',
  },

  // Process Engine Execution Endpoints
  PROCESS_ENGINE_EXECUTION: {
    EXECUTE: '/api/v1/process-engine/execution/execute',
    STATUS: '/api/v1/process-engine/execution/status',
    PAUSE: '/api/v1/process-engine/execution/pause',
    RESUME: '/api/v1/process-engine/execution/resume',
    CANCEL: '/api/v1/process-engine/execution/cancel',
  },

  // Node Activity Log Endpoints
  NODE_ACTIVITY_LOGS: {
    BY_EXECUTION_RES_ID: '/api/v1/process/node-activity-logs/by-execution-res-id',
  },

  // Process Thread Execution History Endpoints (from Process module) — BaseProcessThreadExecutionController,
  // route base api/v1/process/process-thread-executions. "by-process-thread" is the only action with a
  // ProcessThreadID filter. NOTE: this entity's StoppedAt/Duration/TotalNodes/CompletedNodes/FailedNodes/
  // SkippedNodes are written once at creation and never updated on completion — see PROCESS_EXECUTION below.
  PROCESS_THREAD_EXECUTION: {
    BY_PROCESS_THREAD: '/api/v1/process/process-thread-executions/by-process-thread',
    BY_ID: '/api/v1/process/process-thread-executions/by-id',
  },

  // Process Execution Endpoints (from Process module) — the PARENT entity, and the one
  // ProcessExecutionStatusUpdaterService.UpdateExecutionCompletedAsync/UpdateExecutionFailedAsync
  // actually keep up to date (StoppedAt/Duration/TotalNodes/CompletedNodes/FailedNodes/SkippedNodes/
  // ErrorMessage). Used to enrich ProcessThreadExecution rows with real completion data.
  PROCESS_EXECUTION: {
    BY_ID: '/api/v1/process/process-executions/by-id',
  },

  // Process Element Execution Endpoints (from Process module)
  PROCESS_ELEMENT_EXECUTION: {
    SEARCH: '/api/v1/process/process-element-execution/search',
    GET_BY_ID: '/api/v1/process/process-element-execution/get-by-id',
  },

  // Node Forms Endpoints — BaseNodeFormsController
  // GET /api/v1/process-engine/node-forms/standard/GetNodeForms/{nodeSubUsage}/{processElementId}?includeSchema=...&includeChecksum=...
  NODE_FORMS: {
    GET: (nodeSubUsage: string, processElementId: number) =>
      `/api/v1/process-engine/node-forms/standard/GetNodeForms/${encodeURIComponent(nodeSubUsage)}/${processElementId}`,
  },

  // MultiQuery Export Endpoints — BaseFlowEntityExportController
  // Exports flow entities (processes, workflows, elements) in various formats
  MULTIQUERY_EXPORTS: {
    DOWNLOAD_FLOW_ENTITY: '/api/v1/expressions/multiquery/exports/DownloadFlowEntity',
  },

  // Observability Profile Endpoints — BizFirst.Ai.ProcessEngine Observability module
  // Used by the Advanced Execute modal's profile picker (Production/Development/QA/Eval, etc.)
  OBSERVABILITY_PROFILES: {
    LIST: '/api/v1/obs/observability-profiles/list',
  },

  // User Settings Endpoints (IAM Self-Service) — existing, pre-built IAM feature.
  // Used to persist the signed-in user's Advanced Execute default under the
  // "UserObserveSettings" key (see ObservabilityOverride in processEngineExecution.types.ts).
  USER_SETTINGS: {
    BY_KEY: '/api/v1/iam/self-service/user-settings/by-key',
    SET: '/api/v1/iam/self-service/user-settings/set',
  },
} as const;

/**
 * Build full URL from endpoint path
 */
export const buildUrl = (baseUrl: string, endpoint: string): string => {
  // Remove trailing slash from baseUrl
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  // Ensure endpoint starts with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${cleanBaseUrl}${cleanEndpoint}`;
};
